/***
 *
 *   PROJECT ACCESS
 *   Displays organization users and their access across projects
 *
 **********/

import React, { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { Card, AccountNav, Table, Animate, AuthContext, ViewContext } from 'components/lib';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { addProjectUser, getProjects, getProjectUsers, removeProjectUser } from '../../api/projectApi';
import { listOrganizationMembers } from '../../lib/auth-client';

interface OrganizationMember {
  userId: string;
  role: string;
  user?: {
    name?: string;
    email?: string;
  };
}

interface ProjectUser {
  id: string;
}

interface ProjectAccessUser {
  id: string;
  name: string;
  email: string;
  permission: string;
  projects: string[];
}

interface ProjectSummary {
  id: string;
  name: string;
}

type UpdatingAccessMap = Record<string, boolean>;

export function ProjectAccess() {
  const authContext = useContext(AuthContext);
  const viewContext = useContext(ViewContext);
  const [users, setUsers] = useState<ProjectAccessUser[]>([]);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [updatingAccess, setUpdatingAccess] = useState<UpdatingAccessMap>({});

  useEffect(() => {
    let mounted = true;

    async function loadProjectAccess() {
      if (!authContext.user?.session?.activeOrganizationId) {
        if (mounted) {
          setUsers([]);
          setProjects([]);
        }
        return;
      }

      try {
        setLoading(true);

        const [organizationMembers, organizationProjects] = await Promise.all([
          listOrganizationMembers(),
          getProjects(),
        ]);

        const members: OrganizationMember[] = organizationMembers?.members || [];
        const formattedUsers = members.map((member) => ({
          id: member.userId,
          name: member.user?.name || '',
          email: member.user?.email || '',
          permission: member.role,
          projects: [],
        }));

        const formattedProjects = (organizationProjects || []).map((project) => ({
          id: project.id,
          name: project.name,
        }));

        const projectUsersByProject = await Promise.all(
          formattedProjects.map(async (project) => {
            const projectUsers: ProjectUser[] = (await getProjectUsers(project.id)) || [];
            return {
              projectId: project.id,
              userIds: projectUsers.map((user) => user.id),
            };
          })
        );

        const usersWithProjects = formattedUsers.map((user) => ({
          ...user,
          projects: projectUsersByProject
            .filter((project) => project.userIds.includes(user.id))
            .map((project) => project.projectId),
        }));

        if (!mounted) return;

        setUsers(usersWithProjects);
        setProjects(formattedProjects);
      } catch (error) {
        if (!mounted) return;
        setUsers([]);
        setProjects([]);
        viewContext.handleError(error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadProjectAccess();

    return () => {
      mounted = false;
    };
  }, [authContext.user?.session?.activeOrganizationId]);

  const canManageProjectAccess = ['owner', 'admin'].includes(authContext.user?.permission ?? '');

  const handleProjectAccessChange = async (user: ProjectAccessUser, project: ProjectSummary, checked: boolean) => {
    const key = `${project.id}-${user.id}`;

    setUpdatingAccess((current) => ({ ...current, [key]: true }));
    setUsers((currentUsers) =>
      currentUsers.map((currentUser) => {
        if (currentUser.id !== user.id) return currentUser;

        const nextProjects = checked
          ? [...new Set([...currentUser.projects, project.id])]
          : currentUser.projects.filter((projectId) => projectId !== project.id);
        return {
          ...currentUser,
          projects: nextProjects,
        };
      })
    );

    try {
      if (checked) {
        await addProjectUser(String(project.id), String(user.id));
      } else {
        await removeProjectUser(String(project.id), String(user.id));
      }
    } catch (error) {
      setUsers((currentUsers) =>
        currentUsers.map((currentUser) => {
          if (currentUser.id !== user.id) return currentUser;

          const rollbackProjects = checked
            ? currentUser.projects.filter((projectId) => projectId !== project.id)
            : [...new Set([...currentUser.projects, project.id])];
          return {
            ...currentUser,
            projects: rollbackProjects,
          };
        })
      );
      viewContext.handleError(error);
    } finally {
      setUpdatingAccess((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
    }
  };

  const tableData = useMemo(() => {
    const header = [
      { name: 'project_name', title: ' ', sort: true },
      ...users.map((user) => ({
        name: user.id,
        title: user.name,
        hover: user.email,
        sort: false,
      })),
    ];

    const rows: Record<string, React.ReactNode>[] = [
      {
        project_name: '  Role',
        ...users.reduce<Record<string, React.ReactNode>>((acc, user) => {
          acc[user.id] = user.permission;
          return acc;
        }, {}),
      },
    ];

    projects.forEach((project) => {
      rows.push({
        project_name: project.name,
        ...users.reduce<Record<string, React.ReactNode>>((acc, user) => {
          const hasOrganizationWideAccess = authContext.globalPermissions[user.permission]?.admin;
          const hasProjectAccess = hasOrganizationWideAccess || user.projects.includes(project.id);
          const isProjectOwner = user.permission === 'owner';
          const isEditable = canManageProjectAccess && !hasOrganizationWideAccess && !isProjectOwner;
          const updateKey = `${project.id}-${user.id}`;

          acc[user.id] = (
            <input
              type="checkbox"
              disabled={!isEditable || Boolean(updatingAccess[updateKey])}
              checked={hasProjectAccess}
              onChange={(event) => handleProjectAccessChange(user, project, event.target.checked)}
              readOnly={!isEditable}
            />
          );
          return acc;
        }, {}),
      });
    });

    return { header, body: rows };
  }, [authContext.globalPermissions, canManageProjectAccess, projects, updatingAccess, users]);

  return (
    <Fragment>
      <AccountNav />
      <Animate>
        <Card extraClass="form-container" isStat={true} isOverflowAuto style={{ padding: '0px' }}>
          {loading ? (
            <div style={{ height: '400px', display: 'grid', placeItems: 'center' }}>
              <CircularProgress size={32} />
            </div>
          ) : projects.length === 0 ? (
            <Alert sx={{ alignItems: 'center' }} severity="info" className="stat">
              No project found!
            </Alert>
          ) : (
            <div style={{ height: '400px' }}>
              <Table
                data={tableData as unknown as React.ComponentProps<typeof Table>['data']}
                customSpacing={true}
                badge={false as unknown as React.ComponentProps<typeof Table>['badge']}
                loading={loading}
                customStyle={true}
              />
            </div>
          )}
        </Card>
      </Animate>
    </Fragment>
  );
}
